import {
  BRAND_APP_STORE_ACTIONS,
  BRAND_APP_TABS,
  DEFAULT_HOME,
  DEFAULT_IDENTITY,
  DEFAULT_COMPLETION_FIELDS,
  DEFAULT_LOYALTY_BLOCKS,
  DEFAULT_SOCIALS,
  DEFAULT_STORES_SCREEN,
  DEFAULT_TABS,
  createDefaultOrder,
  deriveFeatures,
  type BrandAppHome,
  type BrandAppOfferCard,
  type BrandAppIdentity,
  type BrandAppOrder,
  type BrandAppOrderChannel,
  type BrandAppOrderChannelId,
  type BrandAppSocials,
  type BrandAppStoreActionId,
  type BrandAppStoresScreen,
  type BrandAppTabId,
  type CompletionFieldId,
  type HomeBlockId,
  type LoyaltyBlockId
} from '../lib/brandAppContract'
import { deriveHomeDressing } from '../lib/brandAppHomeDressing'

const defaultIdentity = (): BrandAppIdentity => ({
  ...DEFAULT_IDENTITY,
  pointsConfig: { ...DEFAULT_IDENTITY.pointsConfig }
})
const defaultHome = (): BrandAppHome => ({
  ...DEFAULT_HOME,
  blocks: [...DEFAULT_HOME.blocks],
  blockTitles: { ...DEFAULT_HOME.blockTitles },
  offerIds: [...DEFAULT_HOME.offerIds],
  offerCards: {},
  actus: [],
  dressing: { ...DEFAULT_HOME.dressing }
})
const defaultStoresScreen = (): BrandAppStoresScreen => ({
  ...DEFAULT_STORES_SCREEN,
  hiddenStoreIds: [...DEFAULT_STORES_SCREEN.hiddenStoreIds],
  actions: [...DEFAULT_STORES_SCREEN.actions]
})
const defaultSocials = (): BrandAppSocials => ({ ...DEFAULT_SOCIALS })

const identity = ref<BrandAppIdentity>(defaultIdentity())
const tabs = ref<BrandAppTabId[]>([...DEFAULT_TABS])
const tabOrder = ref<BrandAppTabId[]>([])
const homeBlockOrder = ref<HomeBlockId[]>([])
const home = ref<BrandAppHome>(defaultHome())
const order = ref<BrandAppOrder>(createDefaultOrder())
const storesScreen = ref<BrandAppStoresScreen>(defaultStoresScreen())
const socials = ref<BrandAppSocials>(defaultSocials())
const completionFields = ref<CompletionFieldId[]>([...DEFAULT_COMPLETION_FIELDS])
const loyaltyBlocks = ref<LoyaltyBlockId[]>([...DEFAULT_LOYALTY_BLOCKS])

export default function useBrandAppConfig() {
  const reset = () => {
    identity.value = defaultIdentity()
    tabs.value = [...DEFAULT_TABS]
    tabOrder.value = []
    homeBlockOrder.value = []
    home.value = defaultHome()
    order.value = createDefaultOrder()
    storesScreen.value = defaultStoresScreen()
    socials.value = defaultSocials()
    completionFields.value = [...DEFAULT_COMPLETION_FIELDS]
    loyaltyBlocks.value = [...DEFAULT_LOYALTY_BLOCKS]
  }

  const seed = (initial: Partial<BrandAppIdentity>) => {
    reset()
    identity.value = {
      ...identity.value,
      ...initial,
      brandName: initial.brandName ?? DEFAULT_IDENTITY.brandName,
      logo: initial.logo ?? DEFAULT_IDENTITY.logo,
      primary: initial.primary ?? DEFAULT_IDENTITY.primary
    }
  }

  const updateIdentity = <K extends keyof BrandAppIdentity>(key: K, value: BrandAppIdentity[K]) => {
    identity.value = { ...identity.value, [key]: value }
  }

  const effectiveButtonColor = computed(() => identity.value.buttonColor || identity.value.primary)

  const features = computed(() => deriveFeatures(tabs.value))
  const hasLoyalty = computed(() => tabs.value.includes('loyalty'))

  const enabledTabs = computed(() =>
    tabs.value.map((id) => BRAND_APP_TABS.find((tab) => tab.id === id)).filter((tab) => tab !== undefined)
  )

  const setTabs = (next: BrandAppTabId[]) => {
    if (next.length === 0) return
    tabs.value = next
  }

  const setTabOrder = (next: BrandAppTabId[]) => {
    tabOrder.value = next
  }

  const setHomeBlockOrder = (next: HomeBlockId[]) => {
    homeBlockOrder.value = next
  }

  const defaultTab = computed<BrandAppTabId>(() => (tabs.value.includes('home') ? 'home' : tabs.value[0]))

  const updateHome = <K extends keyof BrandAppHome>(key: K, value: BrandAppHome[K]) => {
    home.value = { ...home.value, [key]: value }
  }

  const setHomeBlocks = (next: HomeBlockId[]) => {
    if (next.length === 0) return
    home.value = { ...home.value, blocks: next }
  }

  const setHomeBlockTitle = (id: HomeBlockId, title: string) => {
    const next = { ...home.value.blockTitles }
    if (title.trim().length === 0) delete next[id]
    else next[id] = title

    home.value = { ...home.value, blockTitles: next }
  }

  const setHomeOfferIds = (next: number[]) => {
    home.value = { ...home.value, offerIds: next }
  }

  const saveHomeOffer = (card: BrandAppOfferCard) => {
    home.value = {
      ...home.value,
      offerIds: home.value.offerIds.includes(card.offerId)
        ? [...home.value.offerIds]
        : [...home.value.offerIds, card.offerId],
      offerCards: { ...home.value.offerCards, [card.offerId]: { ...card } }
    }
  }

  const removeHomeOffer = (id: number) => {
    const offerCards = { ...home.value.offerCards }
    delete offerCards[id]
    home.value = { ...home.value, offerIds: home.value.offerIds.filter((value) => value !== id), offerCards }
  }

  const homeDressing = computed(() => deriveHomeDressing(identity.value.primary, home.value.dressing))

  const updateOrder = <K extends keyof BrandAppOrder>(key: K, value: BrandAppOrder[K]) => {
    order.value = { ...order.value, [key]: value }
  }

  const updateOrderChannel = <K extends keyof BrandAppOrderChannel>(
    id: BrandAppOrderChannelId,
    key: K,
    value: BrandAppOrderChannel[K]
  ) => {
    order.value = {
      ...order.value,
      channels: { ...order.value.channels, [id]: { ...order.value.channels[id], [key]: value } }
    }
  }

  const setOrderChannelStoreUrl = (id: BrandAppOrderChannelId, storeId: number, url: string) => {
    const storeUrls = { ...order.value.channels[id].storeUrls }
    if (url.trim().length === 0) delete storeUrls[storeId]
    else storeUrls[storeId] = url

    updateOrderChannel(id, 'storeUrls', storeUrls)
  }

  const isStoreHidden = (id: number) => storesScreen.value.hiddenStoreIds.includes(id)

  const setStoreVisible = (id: number, visible: boolean) => {
    const next = storesScreen.value.hiddenStoreIds.filter((value) => value !== id)
    storesScreen.value = { ...storesScreen.value, hiddenStoreIds: visible ? next : [...next, id] }
  }

  const isStoreActionEnabled = (id: BrandAppStoreActionId) => storesScreen.value.actions.includes(id)

  const setStoreActionEnabled = (id: BrandAppStoreActionId, enabled: boolean) => {
    const next = storesScreen.value.actions.filter((value) => value !== id)
    storesScreen.value = {
      ...storesScreen.value,
      actions: BRAND_APP_STORE_ACTIONS.map((action) => action.id).filter(
        (value) => next.includes(value) || (enabled && value === id)
      )
    }
  }

  const updateSocials = <K extends keyof BrandAppSocials>(key: K, value: BrandAppSocials[K]) => {
    socials.value = { ...socials.value, [key]: value }
  }

  const setCompletionFields = (next: CompletionFieldId[]) => {
    if (next.length === 0) return
    completionFields.value = next
  }

  const setLoyaltyBlocks = (next: LoyaltyBlockId[]) => {
    if (next.length !== DEFAULT_LOYALTY_BLOCKS.length) return
    loyaltyBlocks.value = next
  }

  return {
    identity,
    tabs,
    defaultTab,
    tabOrder,
    home,
    homeBlockOrder,
    order,
    storesScreen,
    socials,
    completionFields,
    loyaltyBlocks,
    reset,
    seed,
    updateIdentity,
    setTabs,
    setTabOrder,
    updateHome,
    setHomeBlocks,
    setHomeBlockOrder,
    setHomeBlockTitle,
    setHomeOfferIds,
    saveHomeOffer,
    removeHomeOffer,
    updateOrder,
    updateOrderChannel,
    setOrderChannelStoreUrl,
    isStoreHidden,
    isStoreActionEnabled,
    setStoreActionEnabled,
    setStoreVisible,
    updateSocials,
    setCompletionFields,
    setLoyaltyBlocks,
    effectiveButtonColor,
    features,
    hasLoyalty,
    enabledTabs,
    homeDressing
  }
}
